import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  UserPlus,
  Mail,
  Copy,
  CheckCircle2,
  X,
  Send,
  Users,
  Clock,
  Link as LinkIcon,
  Trash2,
} from 'lucide-react'
import {
  createInvitation,
  getOrganizationInvitations,
  cancelInvitation,
  deleteInvitation,
} from '../services/onboarding'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'
import ConfirmDialog from '../components/ConfirmDialog'

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'practitioner', label: 'Practitioner' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'member', label: 'Member' },
]

const InviteTeamMember = () => {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    role: 'member',
  })
  const [copiedToken, setCopiedToken] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const organizationId = useOrganizationId()
  const { isAdmin } = useOrganization()
  const { success, error: showError, warning } = useToast()

  useEffect(() => {
    if (organizationId) {
      fetchInvitations()
    }
  }, [organizationId])

  const fetchInvitations = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const data = await getOrganizationInvitations(organizationId)
      setInvitations(data)
    } catch (error) {
      console.error('Error fetching invitations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async (e) => {
    e.preventDefault()
    if (!organizationId || submitting) return // Prevent double submission

    try {
      setSubmitting(true)
      const email = formData.email.trim().toLowerCase()
      
      const invitation = await createInvitation(
        organizationId,
        email,
        formData.role
      )

      // Generate invitation link
      const inviteLink = `${window.location.origin}/onboarding?invite=${invitation.token}`

      // Check if this was an existing invitation
      if (invitation.isExisting) {
        warning(
          `An invitation for ${email} already exists and is pending. Check the list below to copy the link or cancel it.`,
          {
            title: 'Invitation Already Exists',
            duration: 8000,
          }
        )
        // Don't clear form - let user see what they entered
        setShowForm(false)
      } else if (invitation.emailSent) {
        // Success - email sent
        success(`Invitation created and email sent to ${email}`, {
          title: 'Invitation Sent',
        })
        setFormData({ email: '', role: 'member' })
        setShowForm(false)
      } else {
        // Invitation created but email failed - automatically copy link
        const errorHint = invitation.emailError 
          ? `\n\nError: ${invitation.emailError.message || 'Unknown error'}\n\nPlease check:\n- Resend API key is configured in Supabase\n- Edge Function "resend-email" is deployed\n- From email is verified in Resend`
          : '\n\nPlease check Supabase Edge Function logs for details.'
        
        try {
          await navigator.clipboard.writeText(inviteLink)
          warning(
            `Invitation created for ${email}, but the email could not be sent. The invitation link has been copied to your clipboard - you can paste it to share with the invitee.${errorHint}`,
            {
              title: 'Email Failed - Link Copied',
              duration: 15000,
            }
          )
        } catch (clipboardError) {
          // Fallback if clipboard API fails
          warning(
            `Invitation created for ${email}, but the email could not be sent.${errorHint}\n\nInvitation link: ${inviteLink}`,
            {
              title: 'Email Failed - Copy Link Manually',
              duration: 20000,
            }
          )
        }
        setFormData({ email: '', role: 'member' })
        setShowForm(false)
      }

      fetchInvitations()
    } catch (error) {
      console.error('Error creating invitation:', error)
      
      const errorMessage = error.message || 'Failed to send invitation'
      
      // Handle duplicate invitation error
      if (
        errorMessage.includes('duplicate') ||
        errorMessage.includes('already exists') ||
        errorMessage.includes('unique constraint')
      ) {
        warning(
          `An invitation for ${formData.email} already exists. Please check the invitations list or wait for the current invitation to expire.`,
          {
            title: 'Invitation Already Exists',
            duration: 8000,
          }
        )
        fetchInvitations() // Refresh to show existing invitation
      } else {
        showError(errorMessage, {
          title: 'Invitation Failed',
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async (invitationId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Invitation',
      message: 'Are you sure you want to cancel this invitation? The recipient will no longer be able to accept it.',
      confirmText: 'Cancel Invitation',
      cancelText: 'Keep Invitation',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await cancelInvitation(invitationId)
          success('Invitation cancelled successfully')
          fetchInvitations()
        } catch (error) {
          console.error('Error cancelling invitation:', error)
          showError('Failed to cancel invitation. Please try again.')
        } finally {
          setConfirmDialog(null)
        }
      },
      onClose: () => setConfirmDialog(null),
    })
  }

  const handleDelete = async (invitationId, email) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Invitation',
      message: `Are you sure you want to permanently delete the cancelled invitation for ${email}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setDeleting(true)
          await deleteInvitation(invitationId)
          success('Invitation deleted successfully')
          fetchInvitations()
        } catch (error) {
          console.error('Error deleting invitation:', error)
          showError('Failed to delete invitation. Please try again.')
        } finally {
          setDeleting(false)
          setConfirmDialog(null)
        }
      },
      onClose: () => setConfirmDialog(null),
    })
  }

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/onboarding?invite=${token}`
    navigator.clipboard.writeText(link)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
    success('Invitation link copied to clipboard', { title: 'Copied' })
  }

  const shareInviteLink = async (token, email) => {
    const link = `${window.location.origin}/onboarding?invite=${token}`
    const shareText = `You've been invited to join our organization!\n\nInvitation link: ${link}`
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invitation for ${email}`,
          text: shareText,
          url: link,
        })
        success('Invitation shared successfully', { title: 'Shared' })
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to copy if share fails
          copyInviteLink(token)
        }
      }
    } else {
      // Fallback to copy if share API not available
      copyInviteLink(token)
    }
  }

  const getInviteLink = (token) => {
    return `${window.location.origin}/onboarding?invite=${token}`
  }

  if (!isAdmin) {
    return (
      <div className="section-container">
        <Card variant="soft" className="p-12 text-center">
          <UserPlus size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-600">
            Only administrators can invite team members.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
              <UserPlus size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invite Team Members</h1>
              <p className="text-gray-600">Send invitations to join your organization</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(true)}>
            <Send size={18} className="mr-2" />
            New Invitation
          </Button>
        </div>
      </motion.div>

      {/* Invite Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Send Invitation</h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleInvite} className="space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="colleague@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
                <Select
                  label="Role"
                  options={ROLE_OPTIONS}
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                />
                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={submitting || !formData.email.trim()} 
                    className="flex-1"
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        Send Invitation
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowForm(false)
                      setFormData({ email: '', role: 'member' })
                    }}
                    disabled={submitting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invitations List */}
      <Card variant="soft" className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users size={20} />
          Team Invitations ({invitations.length})
        </h2>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading invitations...</div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-8">
            <Mail size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No invitations yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invitations.map((invitation) => {
              const inviteLink = getInviteLink(invitation.token)
              const isExpired = new Date(invitation.expires_at) < new Date()
              const status = invitation.status || (isExpired ? 'expired' : 'pending')
              
              const getStatusBadge = (status) => {
                switch (status) {
                  case 'accepted':
                    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle2 size={12} /> Accepted</span>
                  case 'expired':
                    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Expired</span>
                  case 'cancelled':
                    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">Cancelled</span>
                  case 'pending':
                  default:
                    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Pending</span>
                }
              }
              
              return (
                <div
                  key={invitation.id}
                  className="p-4 rounded-xl bg-white/60 border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">{invitation.email}</p>
                      <p className="text-sm text-gray-600">
                        Role: <span className="capitalize">{invitation.role}</span>
                        {invitation.accepted_at && (
                          <span className="ml-2">• Accepted {formatDate(invitation.accepted_at)}</span>
                        )}
                      </p>
                    </div>
                    {getStatusBadge(status)}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <Clock size={14} />
                    <span>
                      {status === 'pending' && `Expires: ${formatDate(invitation.expires_at)} • `}
                      Sent: {formatDate(invitation.created_at)}
                    </span>
                  </div>
                  {status === 'pending' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <LinkIcon size={14} className="text-gray-400 dark:text-gray-500" />
                          <input
                            type="text"
                            value={inviteLink}
                            readOnly
                            className="flex-1 text-xs bg-transparent border-none outline-none text-gray-600 dark:text-gray-300"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => copyInviteLink(invitation.token)}
                          className="flex items-center gap-2"
                        >
                          {copiedToken === invitation.token ? (
                            <>
                              <CheckCircle2 size={16} className="text-green-600" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy size={16} />
                              Copy Link
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => shareInviteLink(invitation.token, invitation.email)}
                          className="flex items-center gap-2"
                        >
                          <Send size={16} />
                          Share
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCancel(invitation.id)}
                          className="ml-auto"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  {status === 'cancelled' && (
                    <div className="flex items-center justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDelete(invitation.id, invitation.email)}
                        disabled={deleting}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 size={16} className="mr-1" />
                        {deleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          variant={confirmDialog.variant}
        />
      )}
    </div>
  )
}

export default InviteTeamMember

